import { Fragment, useCallback, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DetailSection } from '@/components/custom/detail-section';
import { SearchInput } from '@/components/custom/search-input';
import { filterByQuery } from '@/lib/filter-by-query';
import { cn } from '@/lib/utils';

interface SearchableListSectionProps<T> {
  title: string;
  items: readonly T[];
  isLoading: boolean;
  getKey: (item: T) => string;
  /** Text fields the search box matches against (see `filterByQuery`). Keep it
   *  referentially stable (module-level) so the filter isn't recomputed per render. */
  searchFields: (item: T) => ReadonlyArray<string | null | undefined>;
  renderItem: (item: T) => React.ReactNode;
  /** Extra header control rendered after the search toggle (e.g. an Add picker). */
  action?: React.ReactNode;
  searchPlaceholder: string;
  loadingLabel: string;
  emptyLabel: string;
  noMatchLabel: string;
  pageSize?: number;
}

/**
 * Detail-sheet section for a short, already-loaded list (a location's providers,
 * a provider's locations): a search toggle in the header that expands a filter
 * box, and client-side pagination over the filtered result.
 *
 * All state is local — give it a `key` of the owning entity's id so switching
 * entities starts from page 1 with the search closed.
 */
export function SearchableListSection<T>({
  title,
  items,
  isLoading,
  getKey,
  searchFields,
  renderItem,
  action,
  searchPlaceholder,
  loadingLabel,
  emptyLabel,
  noMatchLabel,
  pageSize = 5,
}: SearchableListSectionProps<T>): React.ReactElement {
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);

  // Closing the search clears it, so a filter can never be active while hidden.
  const toggleSearch = (): void => {
    setSearchOpen((open) => !open);
    setQuery('');
    setPage(0);
  };

  const handleQueryChange = useCallback((value: string): void => {
    setQuery(value);
    setPage(0);
  }, []);

  const filtered = useMemo(
    () => filterByQuery(items, query, searchFields),
    [items, query, searchFields]
  );

  const totalPages = Math.ceil(filtered.length / pageSize);
  // Clamp to a valid page so removing the last item on the final page can't
  // strand the view on an out-of-range (empty) page.
  const safePage = Math.min(page, Math.max(0, totalPages - 1));
  const pageItems = filtered.slice(safePage * pageSize, (safePage + 1) * pageSize);

  return (
    <DetailSection
      title={title}
      action={
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
              'h-7 w-7 text-muted-foreground',
              searchOpen && 'bg-accent text-foreground'
            )}
            aria-label={`Search ${title.toLowerCase()}`}
            aria-pressed={searchOpen}
            onClick={toggleSearch}
          >
            <Search className="h-3.5 w-3.5" />
          </Button>
          {action}
        </div>
      }
    >
      {searchOpen && (
        <SearchInput
          value={query}
          onValueChange={handleQueryChange}
          placeholder={searchPlaceholder}
          delay={150}
          autoFocus
        />
      )}
      {isLoading ? (
        <div className="text-sm text-muted-foreground">{loadingLabel}</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-muted-foreground">{emptyLabel}</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-muted-foreground">{noMatchLabel}</div>
      ) : (
        <>
          <div>
            {pageItems.map((item) => (
              <Fragment key={getKey(item)}>{renderItem(item)}</Fragment>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-3">
              <span className="text-xs text-muted-foreground">
                {safePage * pageSize + 1}-{Math.min((safePage + 1) * pageSize, filtered.length)} of{' '}
                {filtered.length}
              </span>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  aria-label="Previous page"
                  onClick={() => setPage(Math.max(0, safePage - 1))}
                  disabled={safePage === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  aria-label="Next page"
                  onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
                  disabled={safePage >= totalPages - 1}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </DetailSection>
  );
}
